import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useUnread } from "@/lib/useUnread";
import { GUEST_SECTIONS, sectionForPath } from "@/lib/guestNav";
import { UnreadBadge } from "./UnreadBadge";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Phone, LogOut, Menu, X, BedDouble } from "lucide-react";
import { useGuestBrand } from "@/lib/guestBrand";

const GOLD = "var(--lux-gold, #C6A15B)";

/**
 * One shell for the whole guest portal: five sections in a desktop sidebar,
 * the same five as a mobile tab bar, and a slim top bar carrying the stay
 * badge and the emergency escape hatch.
 */
export default function GuestNav() {
  const { logout } = useAuth();
  const location = useLocation();
  const { clinical_total, partner_total } = useUnread();
  const [open, setOpen] = useState(false);
  const active = sectionForPath(location.pathname);

  const badgeFor = (key) =>
    key === "clinical" ? clinical_total
      : key === "partner" ? partner_total
      : key === "care" ? clinical_total + partner_total
      : 0;

  const sectionLink = (s, onNavigate) => {
    const isActive = active?.id === s.id;
    return (
      <Link
        key={s.id}
        to={s.to}
        onClick={onNavigate}
        data-testid={s.tid}
        className="flex items-start gap-3 rounded-2xl px-3 py-3 mb-1 transition-colors"
        style={{
          background: isActive ? "rgba(198,161,91,0.14)" : "transparent",
          color: isActive ? "var(--lux-ink, #201D17)" : "var(--lux-ink-soft, #6E675A)",
        }}
      >
        <s.icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: isActive ? GOLD : "currentColor" }} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-sm font-semibold">{s.label}</span>
            <UnreadBadge count={badgeFor(s.badge)} testid={`${s.tid}-unread`} />
          </span>
          <span className="block text-[11px] leading-snug mt-0.5 opacity-70 truncate">{s.blurb}</span>
        </span>
      </Link>
    );
  };

  return (
    <>
      {/* Top bar — stay context + emergency, always reachable */}
      <header
        className="fixed top-0 left-0 right-0 z-40 h-12 flex items-center gap-3 px-4 lg:pl-[17.5rem] border-b"
        style={{ background: "var(--lux-cream, #F7F1E8)", borderColor: "rgba(33,59,42,0.10)" }}
        data-testid="guest-topbar"
      >
        <button className="lg:hidden -ml-1 p-1" onClick={() => setOpen(true)} data-testid="guest-nav-open">
          <Menu className="h-5 w-5" />
        </button>
        <StayBadge />
        <div className="ml-auto flex items-center gap-2">
          <LanguageSwitcher />
          <Link
            to="/emergency"
            data-testid="guest-emergency-link"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest"
            style={{ background: "#8C2F1E", color: "#FDF6EC" }}
          >
            <Phone className="h-3 w-3" /> Emergency
          </Link>
        </div>
      </header>

      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r z-50"
        style={{ background: "var(--lux-cream, #F7F1E8)", borderColor: "rgba(33,59,42,0.10)" }}
        data-testid="guest-sidebar"
      >
        <Brand />
        <nav className="flex-1 overflow-y-auto px-3 py-3">
          {GUEST_SECTIONS.map((s) => sectionLink(s))}
        </nav>
        <SignOut onClick={logout} />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-[60]" data-testid="guest-nav-drawer">
          <div className="absolute inset-0" style={{ background: "rgba(32,29,23,0.45)" }}
               onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 flex flex-col"
               style={{ background: "var(--lux-cream, #F7F1E8)" }}>
            <div className="flex items-center justify-between pr-3">
              <Brand />
              <button onClick={() => setOpen(false)} data-testid="guest-nav-close"><X className="h-5 w-5" /></button>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-3">
              {GUEST_SECTIONS.map((s) => sectionLink(s, () => setOpen(false)))}
            </nav>
            <SignOut onClick={logout} />
          </div>
        </div>
      )}

      {/* Mobile tab bar */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t flex"
        style={{ background: "var(--lux-cream, #F7F1E8)", borderColor: "rgba(33,59,42,0.12)" }}
        data-testid="guest-tabbar"
      >
        {GUEST_SECTIONS.map((s) => {
          const isActive = active?.id === s.id;
          const count = badgeFor(s.badge);
          return (
            <NavLink
              key={s.id}
              to={s.to}
              data-testid={`${s.tid}-tab`}
              className="flex-1 flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold"
              style={{ color: isActive ? "var(--lux-ink, #201D17)" : "var(--lux-ink-soft, #6E675A)" }}
            >
              <span className="relative">
                <s.icon className="h-5 w-5" style={{ color: isActive ? GOLD : "currentColor" }} />
                {count > 0 && (
                  <span className="absolute -top-0.5 -right-1 h-2 w-2 rounded-full"
                        style={{ background: GOLD }} data-testid={`${s.tid}-tab-dot`} />
                )}
              </span>
              {s.label}
            </NavLink>
          );
        })}
      </nav>
    </>
  );
}

function Brand() {
  const { brandName, logoUrl, hasBrand } = useGuestBrand();
  if (!hasBrand) {
    return (
      <Link to="/guest" className="block px-5 py-4" data-testid="guest-brand">
        <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: GOLD }}>AllRound</div>
        <div className="lux-serif text-lg leading-tight">Health Concierge</div>
      </Link>
    );
  }
  // The property's crest leads — this should feel like the hotel's own service.
  return (
    <Link to="/guest" className="flex items-start gap-3 px-5 py-4" data-testid="guest-brand">
      {logoUrl && (
        <img src={logoUrl} alt="" data-testid="guest-brand-crest"
             className="h-9 w-9 rounded-lg object-contain shrink-0"
             style={{ background: "#FFFFFF" }} />
      )}
      <span className="min-w-0">
        <span className="block lux-serif text-base leading-tight line-clamp-2">{brandName}</span>
        <span className="block text-[9px] uppercase tracking-[0.22em] mt-0.5"
              style={{ color: GOLD }} data-testid="guest-brand-presented">
          Presented by AllRound
        </span>
      </span>
    </Link>
  );
}

function StayBadge() {
  const { profile } = useAuth();
  const room = profile?.stay?.room_number || profile?.room_number;
  const hotel = profile?.stay?.hotel_name || profile?.hotel_name;
  if (!room && !hotel) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold truncate"
          style={{ color: "var(--lux-ink-soft, #6E675A)" }} data-testid="guest-stay-badge">
      <BedDouble className="h-3.5 w-3.5" style={{ color: GOLD }} />
      {room ? `Room ${room}` : ""}{room && hotel ? " · " : ""}{hotel || ""}
    </span>
  );
}

function SignOut({ onClick }) {
  return (
    <button onClick={onClick} data-testid="guest-nav-signout"
            className="flex items-center gap-2 px-5 py-4 text-xs font-semibold border-t"
            style={{ borderColor: "rgba(33,59,42,0.10)", color: "var(--lux-ink-soft, #6E675A)" }}>
      <LogOut className="h-4 w-4" /> Sign out
    </button>
  );
}
